import { Calculation } from 'final-form-calculate'
import { PeriodoCadastroIndividualEnum } from 'graphql/types.generated'
import { MetaRoot } from 'util/metaPath'
import { isObjectDeepEmpty } from 'util/object'
import { isEmpty } from 'util/validation/Util'

import {
  AcompanhamentoCondicaoSaudeFiltrosAvancadosModel,
  AcompanhamentoCondicaoSaudeFormModel,
  defaultFilterAcompanhamentoCondicaoSaudeByTematica,
  ResultadosTriaEnum,
  TipoVulnerabilidadeEnum,
} from './model/model-acompanhamentoCondicaoSaude'

const createAcompanhamentoCondicaoSaudeProblemasCondicoesFiltrosAvancadosCalculation: (
  meta: MetaRoot<AcompanhamentoCondicaoSaudeFiltrosAvancadosModel>
) => Calculation = (meta) => ({
  field: [meta.grupoCondicao.absolutePath(), meta.problemasCondicoes.absolutePath()],
  updates: {
    [meta.ativoListaProblema.absolutePath()]: (
      _: [],
      allValues: AcompanhamentoCondicaoSaudeFiltrosAvancadosModel,
      prevValues: AcompanhamentoCondicaoSaudeFiltrosAvancadosModel
    ) => {
      return updatesAtivoListaProblemaByProblemasCondicoes(allValues, prevValues)
    },
    [meta.naoIncluidoListaProblema.absolutePath()]: (
      _: [],
      allValues: AcompanhamentoCondicaoSaudeFiltrosAvancadosModel
    ) => {
      return updatesNaoIncluidoListaProblemaByProblemasCondicoes(allValues)
    },
  },
})

const createAcompanhamentoCondicaoSaudeProblemasCondicoesCalculation: (
  meta: MetaRoot<AcompanhamentoCondicaoSaudeFormModel>
) => Calculation = (meta) => ({
  field: [meta.filtrosAvancados.grupoCondicao.absolutePath(), meta.filtrosAvancados.problemasCondicoes.absolutePath()],
  updates: {
    [meta.filtrosAvancados.ativoListaProblema.absolutePath()]: (
      _: [],
      allValues: AcompanhamentoCondicaoSaudeFormModel,
      prevValues: AcompanhamentoCondicaoSaudeFormModel
    ) => {
      return updatesAtivoListaProblemaByProblemasCondicoes(allValues.filtrosAvancados, prevValues.filtrosAvancados)
    },
    [meta.filtrosAvancados.naoIncluidoListaProblema.absolutePath()]: (
      _: [],
      allValues: AcompanhamentoCondicaoSaudeFormModel
    ) => {
      return updatesNaoIncluidoListaProblemaByProblemasCondicoes(allValues.filtrosAvancados)
    },
  },
})

const updatesAtivoListaProblemaByProblemasCondicoes = (
  allValues: AcompanhamentoCondicaoSaudeFiltrosAvancadosModel = {},
  prevValues: AcompanhamentoCondicaoSaudeFiltrosAvancadosModel = {}
) => {
  const previousCondicoesWereEmpty = isEmpty(prevValues.grupoCondicao) && isEmpty(prevValues.problemasCondicoes)
  const currentCondicoesAreEmpty = isEmpty(allValues.grupoCondicao) && isEmpty(allValues.problemasCondicoes)

  if (currentCondicoesAreEmpty) return false
  if (isObjectDeepEmpty(prevValues)) return allValues.ativoListaProblema
  if (previousCondicoesWereEmpty && !currentCondicoesAreEmpty) return true

  return allValues.ativoListaProblema
}

const updatesNaoIncluidoListaProblemaByProblemasCondicoes = (
  allValues: AcompanhamentoCondicaoSaudeFiltrosAvancadosModel = {}
) => {
  const currentCondicoesAreEmpty = isEmpty(allValues.grupoCondicao) && isEmpty(allValues.problemasCondicoes)

  if (currentCondicoesAreEmpty) return false

  return allValues.naoIncluidoListaProblema
}

const createAcompanhamentoCondicaoSaudeTipoVulnerabilidadeCalculation: (
  meta: MetaRoot<AcompanhamentoCondicaoSaudeFormModel>
) => Calculation = (meta) => ({
  field: meta.tipoVulnerabilidadeSelected.absolutePath(),
  updates: {
    [meta.tria.absolutePath()]: (_: [], allValues: AcompanhamentoCondicaoSaudeFormModel) => {
      return allValues.tipoVulnerabilidadeSelected === TipoVulnerabilidadeEnum.TRIA ? allValues.tria : undefined
    },
    [meta.tria.periodoRegistro.absolutePath()]: (_: [], allValues: AcompanhamentoCondicaoSaudeFormModel) => {
      if (allValues.tipoVulnerabilidadeSelected !== TipoVulnerabilidadeEnum.TRIA) return undefined

      return !isEmpty(allValues.tria?.periodoRegistro)
        ? allValues.tria.periodoRegistro
        : PeriodoCadastroIndividualEnum.SEIS_MESES
    },
    [meta.tria.resultadosTria.absolutePath()]: (_: [], allValues: AcompanhamentoCondicaoSaudeFormModel) => {
      if (allValues.tipoVulnerabilidadeSelected !== TipoVulnerabilidadeEnum.TRIA) return undefined

      return !isEmpty(allValues.tria?.resultadosTria)
        ? allValues.tria.resultadosTria
        : ResultadosTriaEnum.RISCO_DE_INSEGURANCA_ALIMENTAR
    },
  },
})

const createAcompanhamentoCondicaoSaudeTematicaCalculation: (
  meta: MetaRoot<AcompanhamentoCondicaoSaudeFormModel>
) => Calculation = (meta) => ({
  field: meta.tematica.absolutePath(),
  updates: {
    [meta.filtrosAvancados.absolutePath()]: (_: [], allValues: AcompanhamentoCondicaoSaudeFormModel) => {
      return defaultFilterAcompanhamentoCondicaoSaudeByTematica[allValues.tematica].filtrosAvancados
    },
    [meta.tria.absolutePath()]: (_: [], allValues: AcompanhamentoCondicaoSaudeFormModel) => {
      return defaultFilterAcompanhamentoCondicaoSaudeByTematica[allValues.tematica].tria
    },
    [meta.tipoVulnerabilidadeSelected.absolutePath()]: (_: [], allValues: AcompanhamentoCondicaoSaudeFormModel) => {
      return defaultFilterAcompanhamentoCondicaoSaudeByTematica[allValues.tematica].tipoVulnerabilidadeSelected
    },
  },
})

const createAcompanhamentoCondicaoSaudeEquipeResponsavelCalculation: (
  meta: MetaRoot<AcompanhamentoCondicaoSaudeFormModel>
) => Calculation = (meta) => ({
  field: meta.equipeResponsavel.absolutePath(),
  updates: {
    [meta.microareas.absolutePath()]: () => null,
  },
})

export const createAcompanhamentoCondicaoSaudeCalculations: (
  meta: MetaRoot<AcompanhamentoCondicaoSaudeFormModel>
) => Calculation[] = (meta) => [
  createAcompanhamentoCondicaoSaudeTematicaCalculation(meta),
  createAcompanhamentoCondicaoSaudeProblemasCondicoesCalculation(meta),
  createAcompanhamentoCondicaoSaudeTipoVulnerabilidadeCalculation(meta),
  createAcompanhamentoCondicaoSaudeEquipeResponsavelCalculation(meta),
]

export const acompanhamentoCondicaoSaudeFiltrosAvancadosCalculations: (
  meta: MetaRoot<AcompanhamentoCondicaoSaudeFiltrosAvancadosModel>
) => Calculation[] = (meta) => [
  createAcompanhamentoCondicaoSaudeProblemasCondicoesFiltrosAvancadosCalculation(meta),
  {
    field: meta.ivcf.buscaPorPontuacao.absolutePath(),
    updates: {
      [meta.ivcf.riscoVulnerabilidade.absolutePath()]: (
        _: [],
        allValues: AcompanhamentoCondicaoSaudeFiltrosAvancadosModel
      ) => {
        if (allValues?.ivcf === undefined) return undefined
        return allValues?.ivcf.buscaPorPontuacao ? undefined : allValues?.ivcf.riscoVulnerabilidade
      },
      [meta.ivcf.pontuacao.absolutePath()]: (_: [], allValues: AcompanhamentoCondicaoSaudeFiltrosAvancadosModel) => {
        if (allValues?.ivcf === undefined) return undefined
        return allValues?.ivcf.buscaPorPontuacao ? allValues?.ivcf.pontuacao : undefined
      },
    },
  },
  {
    field: meta.ivcf.exibirApenasCidadaosIdososSemIvcf.absolutePath(),
    updates: {
      [meta.ivcf.pontuacao.absolutePath()]: (_: [], allValues: AcompanhamentoCondicaoSaudeFiltrosAvancadosModel) => {
        if (!allValues?.ivcf) return undefined
        return allValues?.ivcf.exibirApenasCidadaosIdososSemIvcf ? null : allValues?.ivcf.pontuacao
      },
      [meta.ivcf.riscoVulnerabilidade.absolutePath()]: (
        _: [],
        allValues: AcompanhamentoCondicaoSaudeFiltrosAvancadosModel
      ) => {
        if (!allValues?.ivcf) return undefined
        return allValues?.ivcf.exibirApenasCidadaosIdososSemIvcf ? null : allValues?.ivcf.riscoVulnerabilidade
      },
      [meta.ivcf.subgrupo.absolutePath()]: (_: [], allValues: AcompanhamentoCondicaoSaudeFiltrosAvancadosModel) => {
        if (!allValues?.ivcf) return undefined
        return allValues?.ivcf.exibirApenasCidadaosIdososSemIvcf ? null : allValues?.ivcf.subgrupo
      },
    },
  },
]
